import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, hoy, newId, parseMoney, type Centavos } from '@abastece/shared';
import type { Compra } from '@abastece/core';
import { db } from '../db/dexie.js';
import { emitirYVer } from '../lib/emitir.js';
import { useT } from '../i18n/index.js';
import { Campo } from './Campo.js';
import { ElegirEscribiendo } from './ElegirEscribiendo.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * Una deuda anterior con un proveedor: una factura que la tienda ya debía el día que empezó aquí.
 *
 * One invoice at a time, because that is how suppliers ask to be paid and how «Cuentas por
 * pagar» lists them, each with its own due date. Opened on one already written down, it corrects
 * it: the same event again, saying what it should have said (`compra.saldo_inicial`). What has
 * been paid on it stays paid, so it cannot go below that; with nothing paid, «Quitar» takes it
 * back off.
 */
export function DeudaAnterior({ deuda, onCerrar }: { deuda?: Compra; onCerrar: () => void }) {
  const t = useT();
  // Undefined while Dexie answers, so "add a supplier first" does not flash up on the way.
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), []);
  const [proveedorId, setProveedorId] = useState(deuda?.proveedorId ?? '');
  const [monto, setMonto] = useState(
    deuda ? formatMoney(deuda.totalCordobas, { symbol: false }).replace(/,/g, '') : '',
  );
  const [documento, setDocumento] = useState(deuda?.documento ?? '');
  const [fecha, setFecha] = useState(deuda?.fecha ?? hoy());
  const [vence, setVence] = useState(deuda?.vencePago ?? '');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const cubierto = deuda ? deuda.pagado + (deuda.devueltoACuenta ?? 0) : 0;
  const valor = parseMoney(monto);
  const bajoLoPagado = valor !== null && valor > 0 && cubierto > valor;
  const listo = proveedorId !== '' && valor !== null && valor > 0 && !bajoLoPagado && fecha !== '';
  const elegibles = (proveedores ?? [])
    .filter((p) => p.activo || p.id === proveedorId)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  async function guardar(cuanto: Centavos) {
    setOcupado(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('compra.saldo_inicial', {
        compraId: deuda?.id ?? newId(),
        proveedorId,
        fecha,
        monto: cuanto,
        ...(documento.trim() ? { documento: documento.trim() } : {}),
        ...(vence ? { vencePago: vence } : {}),
      });
      if (rechazo) setError(rechazo);
      else onCerrar();
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal
      titulo={t(deuda ? 'deudaAnterior.corregir' : 'deudaAnterior.titulo')}
      onCerrar={onCerrar}
      pie={
        <div className="flex flex-wrap gap-3">
          {deuda && cubierto === 0 && (
            <button
              type="button"
              className="btn-secundario flex-1 text-rose-300"
              disabled={ocupado}
              onClick={() => void guardar(0 as Centavos)}
            >
              <Ico icono="quitar" />
              {t('comun.quitar')}
            </button>
          )}
          <button
            type="button"
            className="btn-primario flex-1"
            disabled={!listo || ocupado}
            onClick={() => valor !== null && void guardar(valor)}
          >
            <Ico icono="guardar" />
            {t('comun.guardar')}
          </button>
        </div>
      }
    >
      {proveedores !== undefined && elegibles.length === 0 ? (
        <p className="text-sm text-slate-400">
          {t('deudaAnterior.sinProveedores')}{' '}
          <Link to="/proveedores" className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:text-white hover:decoration-2">
            <Ico icono="proveedores" className="size-4" />
            {t('prov.titulo')}
          </Link>
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-400">{t('deudaAnterior.explicacion')}</p>
          <ElegirEscribiendo
            etiqueta={t('gastos.proveedor')}
            placeholder={t('elegir.proveedor')}
            opciones={elegibles}
            value={proveedorId}
            onChange={setProveedorId}
            datos="proveedor"
          />
          <Campo
            etiqueta={t('deudaAnterior.cuanto')}
            inputMode="decimal"
            className="tabular text-lg"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            placeholder="0.00"
            autoComplete="off"
            error={
              bajoLoPagado ? t('deudaAnterior.bajoLoPagado', { monto: formatMoney(cubierto as Centavos) }) : undefined
            }
            ayuda={t('deudaAnterior.ayudaCuanto')}
          />
          <Campo
            etiqueta={t('deudaAnterior.factura')}
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            maxLength={60}
            autoComplete="off"
          />
          <div className="grid grid-cols-2 gap-4">
            <Campo
              etiqueta={t('deudaAnterior.fechaFactura')}
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
            <Campo
              etiqueta={t('deudaAnterior.vence')}
              type="date"
              value={vence}
              onChange={(e) => setVence(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
